import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { permissionCheck } from 'api/global-api';
import { fetchShareLink} from 'api/share-links-api';

import { setPublicToken, getPublicToken } from 'services/public-share-service';

import Spinner from 'components/spinner';
import PublicCTA from 'components/PublicCTA';

import { ShareLink } from 'types';

export type ContextValue = {
    pageConfig: ShareLink['config']
    isPublic: boolean
}

const contextValue: ContextValue = {
    pageConfig: undefined,
    isPublic: false
}

export const PublicPageContext = React.createContext(contextValue);

interface PublicPageProviderProps {
    publicToken: string,
    teamToken?: string,
    type: 'formPreview' | 'form' | 'template' | 'document' | 'tasks' | 'milestones',
    resourceId?: string,
    subresourceId?: string,
    children: React.ReactNode
}

export const PublicPageProvider = ({ publicToken, teamToken, type, resourceId, subresourceId, children }: PublicPageProviderProps) => {
    const { t } = useTranslation('errors');

    const history = useHistory();
    const [pageConfig, setPageConfig] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(true);

    const redirect = () => {
        setPublicToken(null);

        switch (type) {
            case 'document':
                history.replace(`/${resourceId}/documents/root/${subresourceId}`);
                break;

            case 'tasks':
                history.replace(`/${resourceId}/task`);
                break;

            case 'milestones':
                history.replace(`/${resourceId}/roadmap`);
                break;

            default:
                // es lint
        }
    }

    const attemptRedirect = async () => {
        let hasAccess = false;

        if (publicToken) {
            if (resourceId) {
                if (type === 'document') {
                    if (subresourceId) {
                        try {
                            const permissionCheckResult = await permissionCheck({ type: 'document', id: subresourceId, token: teamToken });

                            hasAccess = permissionCheckResult.hasAccess;
                        } catch (e) {}
                    }

                } else {
                    try {
                        const permissionCheckResult = await permissionCheck({ type: 'project', id: resourceId, token: teamToken });
                        hasAccess = permissionCheckResult.hasAccess;
                    } catch (e) {}

                }
            }

            if (hasAccess) {
                redirect();
            }
        }
    };

    if (getPublicToken() !== publicToken && type !== 'template') {
        setPublicToken(publicToken);
    }

    const getShareLink = async () => {
        setLoading(true);
        setError(false);
        try {
            const shareLink = await fetchShareLink('token');

            if (shareLink?.config) {
                setPageConfig(shareLink.config);
            }
        } catch (e) {
            setError(true);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (teamToken) {
            attemptRedirect();
        }

        getShareLink();
    }, [teamToken]);

    const value = useMemo(() => ({
        pageConfig,
        isPublic: true
    }), [pageConfig]);

    if (loading) {
        return <Spinner size="large" centered={true} />;
    }

    if (error) {
        return (
            <div className="maintenance">
                <div className="maintenance-inner">
                    <p>{ t('errors:access.resource') }</p>
                </div>

                <div className="public-page-overlay-floating">
                    <PublicCTA
                        branding={true}
                        onboard={true}
                    />
                </div>
            </div>
        );
    }

    return (
        <PublicPageContext.Provider value={value}>
            { children }
        </PublicPageContext.Provider>
    )
};
